/**
 * planformStore — shared state for the planform overlay in the UVF viewer.
 *
 * Stores an arbitrary editable polygon (chord, span) with optional Y=0
 * symmetry, aircraft mesh opacity, derived reference quantities,
 * area mode selection (gross/net/custom), and user-defined measurement lines.
 *
 * Backend S_gross and S_net are stored as immutable reference values.
 * The displayed polygon area (polygonArea) is recomputed via shoelace
 * on every vertex change — it is the user's editable approximation.
 */

import { create } from "zustand";
import type { WingAreaVizData } from "../flow360/api";

export interface PlanformData {
  vizData: WingAreaVizData;
  liftAxisIdx: number;
  planformAxes: [number, number];
  projZ: number;
}

export type AreaMode = "gross" | "net" | "custom";

export interface Measurement {
  id: string;
  label: string;
  start: [number, number];
  end: [number, number];
}

/** Shoelace formula — returns unsigned area of a simple polygon. */
export function shoelaceArea(verts: [number, number][]): number {
  const n = verts.length;
  if (n < 3) return 0;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const [x1, y1] = verts[i]!;
    const [x2, y2] = verts[(i + 1) % n]!;
    sum += x1 * y2 - x2 * y1;
  }
  return Math.abs(sum) / 2;
}

/**
 * Scan the outline for chord extent at each span bin.
 * Returns { chord, station, le, te } for the bin with max chord (root)
 * and for the outermost bin (tip).
 */
function scanChordProfile(vertices: [number, number][]): {
  root: { chord: number; station: number; le: number; te: number };
  tip: { chord: number; station: number; le: number; te: number };
} {
  const spans = vertices.map((v) => v[1]);
  const sMin = Math.min(...spans);
  const sMax = Math.max(...spans);
  const range = sMax - sMin;
  if (range < 1e-12) {
    return {
      root: { chord: 0, station: sMin, le: 0, te: 0 },
      tip: { chord: 0, station: sMax, le: 0, te: 0 },
    };
  }

  const N = 30;
  const binW = range / N;
  let maxChord = 0;
  let rootBin = { chord: 0, station: sMin, le: 0, te: 0 };
  let tipBin = { chord: 0, station: sMax, le: 0, te: 0 };

  for (let i = 0; i < N; i++) {
    const center = sMin + (i + 0.5) * binW;
    const band = vertices.filter((v) => Math.abs(v[1] - center) <= binW * 0.6);
    if (band.length < 2) continue;
    const le = Math.min(...band.map((v) => v[0]));
    const te = Math.max(...band.map((v) => v[0]));
    const chord = te - le;
    if (chord > maxChord) {
      maxChord = chord;
      rootBin = { chord, station: center, le, te };
    }
    if (i === N - 1) {
      tipBin = { chord, station: center, le, te };
    }
  }

  return { root: rootBin, tip: tipBin };
}

function deriveFromVertices(vertices: [number, number][]) {
  const polygonArea = shoelaceArea(vertices);
  const spans = vertices.map((v) => v[1]);
  const spanMin = Math.min(...spans);
  const spanMax = Math.max(...spans);

  const halfSpan = spanMax - spanMin;
  const oneSided = spanMin >= -1e-6;
  const span = oneSided ? 2 * halfSpan : halfSpan;

  const { root, tip } = scanChordProfile(vertices);
  const rootChord = root.chord;
  const tipChord = tip.chord;
  const smc = halfSpan > 1e-12 ? polygonArea / halfSpan : 0;

  return { polygonArea, span, rootChord, tipChord, smc, rootStation: root.station, tipStation: tip.station };
}

/**
 * Build a stable mirror-index map for full-span outlines.
 * mirrorMap[i] = j means vertex i's symmetric partner is vertex j.
 * Centerline vertices (|span| < eps) map to themselves.
 * Uses nearest-neighbor matching on the mirrored position.
 */
function buildMirrorMap(vertices: [number, number][]): number[] {
  const EPS = 1e-4;
  const map = new Array<number>(vertices.length).fill(-1);
  const paired = new Set<number>();

  for (let i = 0; i < vertices.length; i++) {
    if (paired.has(i)) continue;
    const [c, s] = vertices[i]!;

    if (Math.abs(s) < EPS) {
      map[i] = i;
      paired.add(i);
      continue;
    }

    let bestJ = -1;
    let bestDist = Infinity;
    for (let j = 0; j < vertices.length; j++) {
      if (j === i || paired.has(j)) continue;
      const [cj, sj] = vertices[j]!;
      if (s * sj >= 0) continue;
      const dist = Math.hypot(cj - c, sj + s);
      if (dist < bestDist) {
        bestDist = dist;
        bestJ = j;
      }
    }

    if (bestJ >= 0) {
      map[i] = bestJ;
      map[bestJ] = i;
      paired.add(i);
      paired.add(bestJ);
    } else {
      map[i] = i;
      paired.add(i);
    }
  }

  return map;
}

/**
 * Enforce perfect symmetry: average each mirror pair so both sides
 * have identical chord and |span|.
 */
function symmetrizeVertices(
  vertices: [number, number][],
  mirrorMap: number[],
): [number, number][] {
  const result = vertices.map((v) => [...v] as [number, number]);
  const visited = new Set<number>();

  for (let i = 0; i < result.length; i++) {
    if (visited.has(i)) continue;
    const j = mirrorMap[i] ?? i;
    if (j === i) {
      if (Math.abs(result[i]![1]) < 1e-4) result[i]![1] = 0;
      visited.add(i);
      continue;
    }

    const avgC = (result[i]![0] + result[j]![0]) / 2;
    const avgS = (Math.abs(result[i]![1]) + Math.abs(result[j]![1])) / 2;
    result[i]![0] = avgC;
    result[i]![1] = result[i]![1] > 0 ? avgS : -avgS;
    result[j]![0] = avgC;
    result[j]![1] = result[j]![1] > 0 ? avgS : -avgS;

    visited.add(i);
    visited.add(j);
  }

  return result;
}

/** Check whether an outline straddles span=0 (full-span). */
function isFullSpan(vertices: [number, number][]): boolean {
  let hasPos = false, hasNeg = false;
  for (const [, s] of vertices) {
    if (s > 1e-4) hasPos = true;
    if (s < -1e-4) hasNeg = true;
    if (hasPos && hasNeg) return true;
  }
  return false;
}

function extractOutline(
  outline: number[][], axes: [number, number], closedLoop: boolean,
): [number, number][] {
  const [axC, axS] = axes;
  const raw = closedLoop && outline.length >= 4 ? outline.slice(0, -1) : outline;
  return raw.map((pt) => [pt[axC]!, pt[axS]!]);
}

/** Pick the best available net outline: rasterized_outline > planform_outline. */
function netOutline(vizData: WingAreaVizData, axes: [number, number]): [number, number][] {
  const rast = vizData.rasterized_outline;
  if (rast && rast.length >= 3) {
    return extractOutline(rast, axes, false);
  }
  return extractOutline(vizData.planform_outline, axes, true);
}

/** Pick the best available gross outline: gross_rasterized > trapezoid. */
function grossOutline(vizData: WingAreaVizData, axes: [number, number]): [number, number][] {
  const gross = vizData.gross_rasterized_outline;
  if (gross && gross.length >= 3) {
    return extractOutline(gross, axes, false);
  }
  return extractOutline(vizData.trapezoid_outline, axes, true);
}

let _measureId = 0;

export interface PlanformState {
  visible: boolean;
  toggleVisible: () => void;

  data: PlanformData | null;
  setData: (d: PlanformData) => void;
  clearData: () => void;

  /** Ordered polygon vertices in (chord, span) planform coordinates. */
  vertices: [number, number][];

  /** Area definition mode. */
  areaMode: AreaMode;
  setAreaMode: (mode: AreaMode) => void;
  /** Saved custom vertices for switching back to Custom mode. */
  customVertices: [number, number][];

  /** Mirror edits across span=0. */
  symmetric: boolean;
  setSymmetric: (on: boolean) => void;

  /** Force centerline vertices to span=0 (gross mode). */
  lockCenterline: boolean;
  setLockCenterline: (on: boolean) => void;

  /** Aircraft mesh opacity (0–1). */
  meshOpacity: number;
  setMeshOpacity: (opacity: number) => void;

  /** Backend authoritative values (immutable after detection). */
  backendSGross: number;
  backendSNet: number;

  /** Wing geometry from backend trapezoid (authoritative, excludes fuselage). */
  backendRootChord: number;
  backendTipChord: number;
  backendSpan: number;

  /** Polygon-derived quantities (recomputed on every vertex change). */
  polygonArea: number;
  rootChord: number;
  tipChord: number;
  span: number;
  smc: number;
  /** Span stations where root/tip chord was measured. */
  rootStation: number;
  tipStation: number;

  /** Precomputed mirror index map: mirrorMap[i] = partner of vertex i. */
  mirrorMap: number[];

  /** User-defined measurement lines. */
  measurements: Measurement[];
  addMeasurement: () => void;
  updateMeasurement: (id: string, patch: Partial<Omit<Measurement, "id">>) => void;
  removeMeasurement: (id: string) => void;

  setVertex: (idx: number, chord: number, span: number) => void;
  addVertex: (afterIdx: number, chord: number, span: number) => void;
  removeVertex: (idx: number) => void;
}

export const usePlanformStore = create<PlanformState>((set) => ({
  visible: false,
  toggleVisible: () => set((s) => ({ visible: !s.visible })),

  data: null,
  setData: (d) => {
    const axes: [number, number] = d.planformAxes;
    let defaultVerts = netOutline(d.vizData, axes);
    const trapSpans = d.vizData.trapezoid_outline.map((p) => p[axes[1]]!);
    const trapMin = Math.min(...trapSpans);
    const oneSided = trapMin >= -1e-6;
    let mirrorMap = buildMirrorMap(defaultVerts);
    if (isFullSpan(defaultVerts)) {
      defaultVerts = symmetrizeVertices(defaultVerts, mirrorMap);
    }
    set({
      data: d,
      visible: true,
      areaMode: "net",
      vertices: defaultVerts,
      customVertices: defaultVerts.map((v) => [...v] as [number, number]),
      mirrorMap,
      backendSGross: d.vizData.s_gross,
      backendSNet: d.vizData.s_net,
      backendRootChord: d.vizData.root_chord,
      backendTipChord: d.vizData.tip_chord,
      backendSpan: oneSided ? d.vizData.span_extent * 2 : d.vizData.span_extent,
      ...deriveFromVertices(defaultVerts),
    });
  },
  clearData: () => set({
    data: null, visible: false, vertices: [], customVertices: [],
    areaMode: "net", measurements: [], mirrorMap: [],
    backendSGross: 0, backendSNet: 0,
    backendRootChord: 0, backendTipChord: 0, backendSpan: 0,
  }),

  vertices: [],
  areaMode: "net",
  customVertices: [],

  setAreaMode: (mode) =>
    set((s) => {
      if (!s.data) return {};
      const axes: [number, number] = s.data.planformAxes;
      if (mode === "custom") {
        const custom = s.customVertices.length >= 3
          ? s.customVertices
          : s.vertices.map((v) => [...v] as [number, number]);
        const mMap = buildMirrorMap(custom);
        return {
          areaMode: mode,
          vertices: custom,
          customVertices: custom,
          mirrorMap: mMap,
          ...deriveFromVertices(custom),
        };
      }
      const customSnapshot = s.areaMode === "custom"
        ? s.vertices.map((v) => [...v] as [number, number])
        : s.customVertices;

      let outline = mode === "gross"
        ? grossOutline(s.data.vizData, axes)
        : netOutline(s.data.vizData, axes);
      const mMap = buildMirrorMap(outline);
      if (s.symmetric && isFullSpan(outline)) {
        outline = symmetrizeVertices(outline, mMap);
      }
      return {
        areaMode: mode,
        vertices: outline,
        customVertices: customSnapshot,
        mirrorMap: mMap,
        ...deriveFromVertices(outline),
      };
    }),

  symmetric: true,
  setSymmetric: (on) =>
    set((s) => {
      if (!on) return { symmetric: false };
      const mMap = buildMirrorMap(s.vertices);
      const verts = isFullSpan(s.vertices)
        ? symmetrizeVertices(s.vertices, mMap)
        : s.vertices;
      return {
        symmetric: true,
        mirrorMap: mMap,
        vertices: verts,
        ...deriveFromVertices(verts),
      };
    }),
  lockCenterline: true,
  setLockCenterline: (on) => set({ lockCenterline: on }),
  meshOpacity: 0.15,
  setMeshOpacity: (opacity) => set({ meshOpacity: Math.max(0, Math.min(1, opacity)) }),

  backendSGross: 0,
  backendSNet: 0,
  backendRootChord: 0,
  backendTipChord: 0,
  backendSpan: 0,
  mirrorMap: [],
  polygonArea: 0,
  rootChord: 0,
  tipChord: 0,
  span: 0,
  smc: 0,
  rootStation: 0,
  tipStation: 0,

  measurements: [],
  addMeasurement: () =>
    set((s) => {
      const verts = s.vertices;
      const cx = verts.reduce((a, v) => a + v[0], 0) / (verts.length || 1);
      const cy = verts.reduce((a, v) => a + v[1], 0) / (verts.length || 1);
      const m: Measurement = {
        id: `m-${++_measureId}`,
        label: `Length ${s.measurements.length + 1}`,
        start: [cx - 0.5, cy],
        end: [cx + 0.5, cy],
      };
      return { measurements: [...s.measurements, m] };
    }),
  updateMeasurement: (id, patch) =>
    set((s) => ({
      measurements: s.measurements.map((m) => (m.id === id ? { ...m, ...patch } : m)),
    })),
  removeMeasurement: (id) =>
    set((s) => ({ measurements: s.measurements.filter((m) => m.id !== id) })),

  setVertex: (idx, chord, span) =>
    set((s) => {
      const verts = s.vertices.map((v) => [...v] as [number, number]);
      if (idx < 0 || idx >= verts.length) return {};
      const origSpan = s.vertices[idx]?.[1] ?? span;
      const lockedSpan = s.lockCenterline && Math.abs(origSpan) < 1e-6 ? 0 : span;
      verts[idx] = [chord, lockedSpan];
      if (s.symmetric) {
        const mi = s.mirrorMap[idx] ?? -1;
        if (mi >= 0 && mi !== idx) verts[mi] = [chord, -lockedSpan];
      }
      const update: Partial<PlanformState> = {
        vertices: verts,
        ...deriveFromVertices(verts),
      };
      if (s.areaMode === "custom") update.customVertices = verts;
      return update;
    }),

  addVertex: (afterIdx, chord, span) =>
    set((s) => {
      const verts = [...s.vertices];
      const insertIdx = Math.min(afterIdx + 1, verts.length);
      verts.splice(insertIdx, 0, [chord, span]);
      const newMap = buildMirrorMap(verts);
      const update: Partial<PlanformState> = {
        vertices: verts,
        mirrorMap: newMap,
        ...deriveFromVertices(verts),
      };
      if (s.areaMode === "custom") update.customVertices = verts;
      return update;
    }),

  removeVertex: (idx) =>
    set((s) => {
      if (s.vertices.length <= 3) return {};
      const verts = [...s.vertices];
      const mi = s.symmetric ? (s.mirrorMap[idx] ?? -1) : -1;
      // Remove in descending index order to keep indices valid
      const toRemove = mi >= 0 && mi !== idx ? [idx, mi].sort((a, b) => b - a) : [idx];
      if (s.vertices.length - toRemove.length < 3) return {};
      for (const ri of toRemove) verts.splice(ri, 1);
      const newMap = buildMirrorMap(verts);
      const update: Partial<PlanformState> = {
        vertices: verts,
        mirrorMap: newMap,
        ...deriveFromVertices(verts),
      };
      if (s.areaMode === "custom") update.customVertices = verts;
      return update;
    }),
}));
